import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { SimpleUserWithLocation } from '@features/whereabouts/models/movements.models';
import { getUsersInOfficeOn } from '@features/whereabouts/services/Movements';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { selectActiveDay } from "../../../../store/editMovements.duck";
import { FloorContainer } from "./AreaAndDeskUsageView";
import { OfficeListUserRow } from "./OfficeListUserRow";
import { OpenOfficeUser, usersInOpenOffice } from "./openOfficeUsers";

export function OpenOfficeUsageView(props: Props) {
  const {rootOffice, floors} = props;
  const activeDate = useSelector(selectActiveDay);
  const [usersInOffice, setUsersInOffice] = useState<SimpleUserWithLocation[]>([]);
  const {t} = useTranslation();

  useEffect(() => {
    setUsersInOffice([]);
    if (!activeDate) return;

    let isCurrent = true;
    getUsersInOfficeOn(activeDate, rootOffice.id)
      .then((resp: SimpleUserWithLocation[]) => {
        if (isCurrent) setUsersInOffice(resp);
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
    };
  }, [activeDate, rootOffice.id]);

  const openOfficeUsers = useMemo(() => usersInOpenOffice(usersInOffice, rootOffice, floors),
    [usersInOffice, rootOffice, floors]);

  if (openOfficeUsers.length === 0) {
    return null;
  }

  return (
    <>
      <HeadlineSmall style={{marginBottom: 16, marginTop: 48}}>{t('open-office')}</HeadlineSmall>
      <FloorContainer data-test="open-office-container">
        {/* The office or floor name stands in for a desk label, so rows read "Floor 2" or "Floor 2 - AM" */}
        {openOfficeUsers.map(({user, amOffice, pmOffice}: OpenOfficeUser, key: number) => (
          <OfficeListUserRow key={key} containerOffice={rootOffice}
                             firstName={user.firstName}
                             lastName={user.lastName}
                             amDeskLabel={amOffice?.label}
                             pmDeskLabel={pmOffice?.label}
                             fireWarden={user.fireWarden}
                             firstAider={user.firstAider}
                             mentalHealthAider={user.mentalHealthAider}
                             keyHolder={user.keyHolder} />
        ))}
      </FloorContainer>
    </>
  )
}

interface Props {
  rootOffice: OfficeEntity;
  floors: OfficeEntity[];
}
